import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { serviceDetails } from "../data/serviceDetails";

export const ServiceDetail = () => {
  const { slug } = useParams();
  const service = slug ? serviceDetails[slug] : null;

  if (!service) {
    const notFoundTitle = "Service introuvable | Weblax";
    const notFoundDescription =
      "Le service demandé est introuvable. Consultez l'ensemble des services Weblax : création de site internet, SEO, e-commerce, branding et marketing digital.";
    const notFoundUrl = `https://weblax.fr/services/${slug || ""}`;

    return (
      <>
        <Helmet>
          <html lang="fr" />
          <title>{notFoundTitle}</title>
          <meta name="description" content={notFoundDescription} />
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={notFoundUrl} />

          <meta property="og:type" content="website" />
          <meta property="og:locale" content="fr_FR" />
          <meta property="og:site_name" content="Weblax" />
          <meta property="og:title" content={notFoundTitle} />
          <meta property="og:description" content={notFoundDescription} />
          <meta property="og:url" content={notFoundUrl} />
          <meta property="og:image" content="https://weblax.fr/og-image.jpg" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={notFoundTitle} />
          <meta name="twitter:description" content={notFoundDescription} />
          <meta name="twitter:image" content="https://weblax.fr/og-image.jpg" />
        </Helmet>

        <div className="weblax-container py-24">
          <h1 className="text-3xl font-bold mb-4">Service introuvable</h1>
          <Link to="/services" className="text-primary hover:underline">
            Retour à la page services
          </Link>
        </div>
      </>
    );
  }

  const Icon = service.icon;

  const pageTitle = `${service.title} | Weblax - Service digital sur mesure`;
  const pageDescription =
    service.description ||
    `Découvrez notre service ${service.title} chez Weblax. Une solution sur mesure pour développer votre présence digitale, améliorer vos performances et atteindre vos objectifs.`;
  const pageUrl = `https://weblax.fr/services/${slug}`;
  const pageImage = "https://weblax.fr/og-image.jpg";

  const serviceStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: pageDescription,
    url: pageUrl,
    provider: {
      "@type": "Organization",
      name: "Weblax",
      url: "https://weblax.fr",
      logo: "https://weblax.fr/logo.png",
      email: "contact@weblax.fr",
      telephone: "+33685802016",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Paris",
        addressCountry: "FR",
      },
    },
    areaServed: "FR",
    serviceType: service.title,
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://weblax.fr/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Services",
        item: "https://weblax.fr/services",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: service.title,
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content={`${service.title.toLowerCase()}, agence web, services digitaux, création site internet, référencement SEO, développement web, Weblax`}
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(serviceStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      </Helmet>

      <div data-testid="service-detail-page">
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28">
              <div className="max-w-3xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h1 className="weblax-heading-display">{service.title}</h1>
                </div>

                <p className="weblax-body-lg">{service.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="weblax-section">
          <div className="weblax-container">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold mb-4">À propos de ce service</h2>
                <p className="text-muted-foreground mb-8">{service.intro}</p>

                <h3 className="text-xl font-semibold mb-4">Bénéfices</h3>
                <div className="space-y-3">
                  {service.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Card className="rounded-3xl">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="text-xl font-semibold mb-4">Ce qui est inclus</h3>

                    <div className="space-y-3">
                      {service.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 text-muted-foreground"
                        >
                          <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                      <Link to="/devis" className="w-full">
                        <Button className="w-full">
                          Demander un devis
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>

                      <Link to="/services" className="w-full">
                        <Button variant="outline" className="w-full">
                          Retour aux services
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="bg-primary rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
              <div className="relative max-w-2xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Vous souhaitez en parler ?
                </h2>
                <p className="text-lg text-white/80 mb-8">
                  Nous pouvons vous conseiller et vous proposer une solution adaptée
                  à votre activité.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/devis">
                    <Button className="bg-white text-primary hover:bg-white/90 px-8 py-3 rounded-full font-medium">
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 px-8 py-3 rounded-full"
                    >
                      Nous contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServiceDetail;